import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Text, useToast } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import EyeIcon from '../../../../../assets/eye-icon.svg';
import { useNavigate } from 'react-router-dom';
import PaginationComponent from '../../../../../components/Pagination';
import NoDataComponent from '../../../../../components/NoData';
import TableComponent from '../../../../../components/Table';
import InputSearchComponent from '../../../../../components/Common/Form/input-search';
import { formatDate } from '../../../../../utils/helper.util';
import { listIgnoredWriters, unignoreWriter } from '../../../../../apis/article.api';
import ToastBtnMessage from '../../../../../components/ToastBtnMessage';
import AvatarIcon from '../../../../../assets/medium_default_optimized.png';

export const IgnoredWritersTableComponent = () => {
  const navigate = useNavigate();
  const [dataTable, setDataTable] = useState([]);
  const [pagination, setPagination] = useState({
    limit: 6,
    page: 1,
    sortBy: undefined,
    sort: undefined,
    search: undefined
  });
  const toast = useToast();

  const params = {
    ...pagination
  };

  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );

  const {
    data: getListIgnoredWriters,
    isFetching,
    refetch
  } = useQuery({
    queryKey: ['getListIgnoredWriters', filteredParams],
    queryFn: () => listIgnoredWriters(filteredParams)
  });

  const { mutateAsync: postUnignoreWriter } = useMutation({
    mutationFn: unignoreWriter
  });

  const handleChangePage = (page) => {
    setDataTable(() => []);
    setPagination((pre) => {
      return {
        ...pre,
        page
      };
    });
  };

  const handleChangeSearch = (search) => {
    setPagination((pre) => {
      return {
        ...pre,
        page: 1,
        search: search
      };
    });
  };

  const handleUnignoreWriter = async (id) => {
    try {
      await postUnignoreWriter(id);
      toast({
        render: () => (
          <ToastBtnMessage
            title={'Successfully unignored the writer!'}
            isError={false}
            color="jet-black"
            isHiddenButton
          />
        ),
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const convertWriters = useCallback(
    (row) => {
      return (
        <Box
          display={'flex'}
          alignItems={'center'}
          cursor={'pointer'}
          gap={2}
          onClick={() => navigate(`/${row?.username}`)}
        >
          <img
            src={row?.imgUrl || AvatarIcon}
            className="w-6 h-6 rounded-full object-cover"
            alt=""
          />
          <Box display={'flex'} flexDirection={'column'}>
            <Text
              fontSize={'xs'}
              fontWeight={600}
              _hover={{
                color: '#4CAF50'
              }}
              transition={'0.2s'}
            >
              {row?.firstName}
            </Text>
            {row?.username && (
              <span className="font-[600] text-[10px] leading-[12px] text-02-dim-gray">
                @{row?.username}
              </span>
            )}
          </Box>
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const convertAction = useCallback((row) => {
    return (
      <div
        className="flex gap-2 items-center cursor-pointer hover:opacity-80 w-fit"
        onClick={() => handleUnignoreWriter(row?.id)}
        title="Unignore Writer"
      >
        <img src={EyeIcon} className="" alt="icon" />
        <p className="text-growth-green font-[900] text-sm">Unignore</p>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (sortName) => {
    let objSort = {
      sortBy: pagination.sortBy,
      sort: pagination.sort
    };
    if (sortName !== objSort.sortBy) objSort = { sortBy: sortName, sort: 'asc' };
    else if (sortName === objSort.sortBy && objSort.sort === 'asc')
      objSort = {
        ...objSort,
        sort: 'desc'
      };
    else if (sortName === objSort.sortBy && objSort.sort === 'desc')
      objSort = {
        ...objSort,
        sort: 'asc'
      };
    setPagination({ ...pagination, ...objSort });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Writers',
        accessor: (row) => convertWriters(row),
        id: 'username',
        isSortField: true,
        widthCustom: '18rem',
        minWidthCustom: '18rem'
      },
      {
        Header: 'Date Ignored',
        accessor: (row) => <Text fontSize={'xs'}>{formatDate(row?.blockedAt, true)}</Text>,
        id: 'blockedAt',
        widthCustom: '10rem',
        minWidthCustom: '10rem',
        isSortField: true
      },
      {
        Header: 'Actions',
        accessor: (row) => convertAction(row),
        widthCustom: '12rem',
        minWidthCustom: '12rem',
        id: 'action'
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [convertWriters, convertAction, handleSort]
  );

  useEffect(() => {
    if (getListIgnoredWriters) {
      setDataTable(getListIgnoredWriters?.items || []);
    }
  }, [getListIgnoredWriters]);

  return (
    <Box
      w={'full'}
      border={'1px solid'}
      borderColor={'anti-flash-lite'}
      borderRadius={{ base: 0, md: '12px' }}
      shadow={'outline'}
    >
      <Box
        bg={'white'}
        p={{ base: '16px', md: '16px 24px' }}
        display={'flex'}
        alignItems={'start'}
        borderTopRadius={'12px'}
        justifyContent={'space-between'}
        overflow={'auto'}
      >
        <Box
          display={'flex'}
          gap="12px"
          flexDirection={{ base: 'column', md: 'row' }}
          width={'full'}
          overflow={'auto'}
        >
          <InputSearchComponent
            setPaginationSearch={handleChangeSearch}
            placeholder={'Search'}
            setLoading={() => {}}
            width={{ base: '100%', md: '320px' }}
          />
        </Box>
      </Box>
      <Box
        bg={'white'}
        rounded={'0 0 0.75rem 0.75rem'}
        display={'flex'}
        minH={'360px'}
        flexFlow={'column'}
        overflowY={'hidden'}
        overflowX={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            display: 'block',
            height: '8px', // For horizontal scrollbar
            width: '8px' // For vertical scrollbar
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e6e7eb',
            borderRadius: '30px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f5f5f5'
          }
        }}
      >
        <TableComponent
          isLoading={isFetching}
          columns={columns}
          data={dataTable || []}
          headerHeight={'44px'}
          fontsizeTable={'sm'}
          handleSort={handleSort}
          currentSort={{ sortBy: pagination.sortBy, sort: pagination.sort }}
          haveFooter={false}
        />
        {dataTable?.length === 0 && !isFetching ? (
          <Box minH={'360px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <NoDataComponent
              title="It looks like you haven’t ignored any writers yet."
              isFetching={isFetching}
            />
          </Box>
        ) : null}
      </Box>

      {getListIgnoredWriters?.totalPage > 0 ? (
        <Box
          borderBottomRadius={'12px'}
          p={'1.25rem 1.5rem'}
          mt={'auto'}
          borderTop="1px solid"
          borderColor={'anti-flash-lite'}
          background={'light.100'}
        >
          <PaginationComponent
            currentPage={pagination.page}
            totalPages={getListIgnoredWriters?.totalPage}
            onChangePage={handleChangePage}
          />
        </Box>
      ) : null}
    </Box>
  );
};
